import React, { useState } from 'react';
import { Sparkles, Loader2, Wand2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { adminAPI } from '../services/api';

const CONF = { haute: 'bg-green-100 text-green-800', moyenne: 'bg-amber-100 text-amber-800', faible: 'bg-red-100 text-red-700' };

// "Assistant de fiche IA": brand + ref (+ optional reference URL) → original draft prefilled into the product form
const ProductAiAssist = ({ form, onDraft }) => {
  const [open, setOpen] = useState(!form.name);
  const [brand, setBrand] = useState(form.brand || '');
  const [ref, setRef] = useState(form.ref || '');
  const [hint, setHint] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const run = async () => {
    if (brand.trim().length < 2 || ref.trim().length < 2) { setError('Indiquez la marque et la référence fabricant.'); return; }
    setBusy(true); setError(''); setResult(null);
    try {
      const d = await adminAPI.productAssist({ brand: brand.trim(), ref: ref.trim(), hint: hint.trim(), reference_url: url.trim() || null });
      setResult(d);
      onDraft(d);
    } catch (e) {
      setError(e.response?.data?.detail || "L'assistant IA est indisponible pour le moment");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white" data-testid="product-ai-assist">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="product-ai-assist-toggle">
        <span className="flex items-center gap-2 font-semibold text-violet-900"><Sparkles size={18} className="text-violet-600" /> Assistant de fiche IA <span className="text-xs font-normal text-violet-700">— marque + référence → fiche originale pré-remplie</span></span>
        {open ? <ChevronUp size={16} className="text-violet-600" /> : <ChevronDown size={16} className="text-violet-600" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input data-testid="ai-assist-brand" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Marque fabricant (ex : BOSCH)" />
            <Input data-testid="ai-assist-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Référence (ex : 0 986 494 235)" />
            <Input data-testid="ai-assist-hint" value={hint} onChange={(e) => setHint(e.target.value)} placeholder="Indication (ex : plaquettes avant Clio 4)" />
          </div>
          <Input data-testid="ai-assist-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="URL de référence (optionnel) — lue uniquement pour vérifier références / OE / caractéristiques, jamais copiée" className="text-xs" />
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" data-testid="ai-assist-run" onClick={run} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Wand2 size={16} className="mr-2" />} {busy ? 'Rédaction en cours…' : 'Générer la fiche par IA'}
            </Button>
            <p className="text-[11px] text-violet-800/80">Description 100 % originale, catégorie, caractéristiques, numéros OE et compatibilités <strong>proposés à vérifier</strong>. Prix et photos restent à saisir.</p>
          </div>
          {error && <p className="text-sm text-red-600 flex items-center gap-1" data-testid="ai-assist-error"><AlertTriangle size={14} /> {error}</p>}
          {result && (
            <div className="rounded-lg bg-white border border-violet-100 p-3 text-sm space-y-2" data-testid="ai-assist-result">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-gray-800">Fiche pré-remplie ci-dessous.</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CONF[result.confidence] || CONF.moyenne}`} data-testid="ai-assist-confidence">Confiance IA : {result.confidence}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-800" data-testid="ai-assist-compat-count">{result.compatibility?.length || 0} compatibilité(s) véhicule proposée(s){result.page_compat_count ? ` — dont ${result.page_compat_count} relevée(s) sur la page de référence` : ''}</span>
                {result.price_hint?.max > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700" data-testid="ai-assist-price-hint">Prix marché observé : {result.price_hint.min}–{result.price_hint.max} € TTC</span>}
                {result.notes?.map((n, i) => <span key={i} className="text-xs text-gray-500">{n}</span>)}
              </div>
              {result.a_verifier?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle size={12} /> À vérifier avant publication</p>
                  <ul className="list-disc pl-5 text-xs text-gray-700 mt-1 space-y-0.5" data-testid="ai-assist-checks">{result.a_verifier.map((x, i) => <li key={i}>{x}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductAiAssist;
